import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DayPicker, { DateUtils } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import 'react-day-picker/lib/style.css';
import UserStore from '../stores/UserStore';
import { I18nFormatter } from '../utils/I18n/I18n';
import { getLocale } from '../utils/I18n/Formatter/helpers';
import { withTranslation } from './ChildrenContext.js';

/**
 * The calendar (react-day-picker 7) works with the dates of the browser timezone. A moment of the selected
 * timezone is turned into a browser Date of the same calendar day (wall clock), at noon, so that the day does not
 * depend on the browser offset or on DST (before: the end of a day in UTC, e.g. 23:59:59, shown with the current
 * browser offset moved to the next day for the dates before the DST change).
 */
const _getDate = (date, timeZone) => {
    if (!(date instanceof moment)) {
        return date;
    }
    const zoned = timeZone ? date.clone().tz(timeZone) : date;

    return new Date(zoned.year(), zoned.month(), zoned.date(), 12);
};

const _getTimeZone = (props) => props.timezone || UserStore.getValidTimeZone();

/**
 * moment formats of the date of the interface language, the same as I18nFormatter.date shows it (Intl, e.g.
 * 30.10.2026 for uk / ru, 10/30/2026 for en): the strict one and the one with 1-digit day / month.
 */
const _getInputFormats = () => {
    let parts = [];
    try {
        parts = Intl.DateTimeFormat(getLocale(), {}).formatToParts(new Date(2026, 10, 25));
    } catch (e) {
        parts = [];
    }
    if (!parts.length) {
        return ['DD.MM.YYYY', 'D.M.YYYY'];
    }
    const build = (short) => parts.map((part) => {
        switch (part.type) {
            case 'day': return short ? 'D' : (part.value.length === 2 ? 'DD' : 'D');
            case 'month': return short ? 'M' : (part.value.length === 2 ? 'MM' : 'M');
            case 'year': return 'YYYY';
            default: return `[${part.value}]`;
        }
    }).join('');

    return [build(false), build(true)];
};

// a moment / null by the typed text: the calendar day of the selected timezone
const _parseInput = (text, timeZone) => {
    const value = (text || '').trim();
    if (!value) {
        return null;
    }
    const parsed = moment.tz(value, _getInputFormats(), true, timeZone);

    return parsed.isValid() ? parsed : null;
};

const _sameDay = (a, b) => (!a && !b) || (a && b && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth() && a.getDate() === b.getDate());

class DayPickerInputRange extends React.Component {
    constructor(props) {
        super(props);

        const from = _getDate(props.from, _getTimeZone(props));
        const to = _getDate(props.to, _getTimeZone(props));

        this.state = {
            valueFrom: this.format(from),
            valueTo: this.format(to),
            invalidFrom: false,
            invalidTo: false,
            from: from,
            to: to,
            enteredTo: to
        };

        this.handleDayClick = this.handleDayClick.bind(this);
        this.onChangeInput = this.onChangeInput.bind(this);
        this.onCommitInput = this.onCommitInput.bind(this);
        this.onKeyDownInput = this.onKeyDownInput.bind(this);
        this.nextShowMonths = this.nextShowMonths.bind(this);
        this.prevShowMonths = this.prevShowMonths.bind(this);
        this.renderNavbarLeft = this.renderNavbarLeft.bind(this);
        this.renderNavbarRight = this.renderNavbarRight.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const from = _getDate(nextProps.from, _getTimeZone(nextProps));
        const to = _getDate(nextProps.to, _getTimeZone(nextProps));
        const prevFrom = _getDate(this.props.from, _getTimeZone(this.props));
        const prevTo = _getDate(this.props.to, _getTimeZone(this.props));

        // the parent is re-rendered often (dropdown portal): reset only when the range / timezone really changed,
        // otherwise the typed text and a half-selected range would be lost
        if (_sameDay(from, prevFrom) && _sameDay(to, prevTo) && _getTimeZone(nextProps) === _getTimeZone(this.props)) {
            return;
        }

        this.setState({
            valueFrom: this.format(from),
            valueTo: this.format(to),
            invalidFrom: false,
            invalidTo: false,
            from: from,
            to: to,
            enteredTo: to
        }, () => {
            this.showMonths();
        });
    }

    // date: a browser Date of a calendar day (see _getDate) - formatted as that day of the selected timezone
    format(date, timeZone = null) {
        if (null === timeZone) {
            timeZone = _getTimeZone(this.props);
        }
        if (!date) {
            return '';
        }
        const zoned = moment.tz({
            year: date.getFullYear(),
            month: date.getMonth(),
            date: date.getDate(),
            hour: 12
        }, timeZone);

        return I18nFormatter.date(zoned.toDate(), timeZone);
    }

    isSelectingFirstDay(from, to, day) {
        const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
        const isRangeSelected = from && to;
        return !from || isBeforeFirstDay || isRangeSelected;
    }

    handleDayClick(day) {
        if (this.isSelectingFirstDay(this.state.from, this.state.to, day)) {
            this.setState({
                from: day,
                to: null,
                enteredTo: null,
                valueFrom: this.format(day),
                valueTo: '',
                invalidFrom: false,
                invalidTo: false
            });
        } else {
            this.setState({
                to: day,
                enteredTo: day,
                valueTo: this.format(day),
                invalidFrom: false,
                invalidTo: false
            }, () => {
                this.onUpdateRange();
            });
        }
    }

    onUpdateRange() {
        const from = moment.tz({
            year: this.state.from.getFullYear(),
            month: this.state.from.getMonth(),
            date: this.state.from.getDate()
        }, this.props.timezone).startOf('day');
        const to = moment.tz({
            year: this.state.to.getFullYear(),
            month: this.state.to.getMonth(),
            date: this.state.to.getDate()
        }, this.props.timezone).endOf('day');

        this.props.onAction && this.props.onAction(from, to);
    }

    // manual input: the text is kept while typing, the date is applied on Enter / leaving the field
    onChangeInput(event) {
        const field = event.target.getAttribute('data-field');
        this.setState(field === 'from'
            ? {valueFrom: event.target.value, invalidFrom: false}
            : {valueTo: event.target.value, invalidTo: false});
    }

    onKeyDownInput(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.onCommitInput(event);
        }
    }

    onCommitInput(event) {
        const field = event.target.getAttribute('data-field');
        const timeZone = _getTimeZone(this.props);
        const text = field === 'from' ? this.state.valueFrom : this.state.valueTo;
        const parsed = _parseInput(text, timeZone);
        const current = field === 'from' ? this.state.from : this.state.to;

        if (!parsed) {
            // an empty / unchanged field is not an error
            if (!(text || '').trim() || (current && text === this.format(current))) {
                return;
            }
            this.setState(field === 'from' ? {invalidFrom: true} : {invalidTo: true});
            this.props.onInvalid && this.props.onInvalid();

            return;
        }

        const day = new Date(parsed.year(), parsed.month(), parsed.date(), 12);
        const from = field === 'from' ? day : this.state.from;
        const to = field === 'to' ? day : this.state.to;
        // "from" later than "to": the "to" field is marked
        const invalidRange = !!(from && to && DateUtils.isDayAfter(from, to));

        this.setState({
            from: from,
            to: to,
            enteredTo: to,
            valueFrom: field === 'from' ? this.format(day) : this.state.valueFrom,
            valueTo: field === 'to' ? this.format(day) : this.state.valueTo,
            invalidFrom: false,
            invalidTo: invalidRange
        }, () => {
            this.showMonths(field === 'to' ? day : null);
            if (invalidRange) {
                this.props.onInvalid && this.props.onInvalid();
            } else if (this.state.from && this.state.to) {
                this.onUpdateRange();
            }
        });
    }

    // the left calendar shows the month of "from" (or of the given day, e.g. a typed "to" in another month)
    showMonths(day = null) {
        const start = day && this.state.from && moment(day).diff(moment(this.state.from), 'months') >= 2
            ? moment(day).subtract(1, 'months').toDate()
            : (this.state.from || day);
        if (!start || !this.refs.leftMonth || !this.refs.rightMonth) {
            return;
        }
        this.refs.leftMonth.showMonth(start);
        this.refs.rightMonth.showMonth(moment(start).add(1, 'months').toDate());
    }

    prevShowMonths() {
        this.refs.leftMonth.showPreviousMonth();
        this.refs.rightMonth.showPreviousMonth();
    }

    nextShowMonths() {
        this.refs.leftMonth.showNextMonth();
        this.refs.rightMonth.showNextMonth();
    }

    renderNavbarLeft({className}) {
        return (
            <div className={className}>
                <span className="DayPicker-NavButton ranges-calendar__nav-left" onClick={this.prevShowMonths}>
                    <i className="fa fa-chevron-left" />
                </span>
            </div>
        );
    }

    renderNavbarRight({className}) {
        return (
            <div className={className}>
                <span className="DayPicker-NavButton ranges-calendar__nav-right" onClick={this.nextShowMonths}>
                    <i className="fa fa-chevron-right" />
                </span>
            </div>
        );
    }

    render() {
        const locale = UserStore.getLocale();

        // the state keeps browser Dates of the calendar days (see _getDate): no shifting is needed
        const fixedFrom = this.state.from || null;
        const fixedEnteredTo = this.state.enteredTo || null;

        // manual input: an example of the format (today) and the hint
        const dataPicker = (this.props.t && this.props.t.dataTable && this.props.t.dataTable.dataPicker) || {};
        const placeholder = this.format(new Date());
        const hint = dataPicker.inputHint ? dataPicker.inputHint.replace('%example%', placeholder) : placeholder;

        return (
            <div className="reactdaypicker">
                <div className="reactdaypicker-item">
                    <div className="reactdaypicker-field">
                        <input className={`form-control reactdaypicker-field__input${this.state.invalidFrom ? ' is-invalid' : ''}`} type="text" name="" data-field="from"
                            value={this.state.valueFrom} placeholder={placeholder} title={this.state.invalidFrom ? (dataPicker.invalidDate || '') : hint}
                            inputMode="numeric" autoComplete="off"
                            onChange={this.onChangeInput} onBlur={this.onCommitInput} onKeyDown={this.onKeyDownInput} />
                        <i className="fa fa-calendar glyphicon glyphicon-calendar reactdaypicker-field__icon" />
                        <div className="calendar-time" />
                    </div>
                    <DayPicker
                        ref={'leftMonth'}
                        className={'reactdaypicker__range'}
                        numberOfMonths={1}
                        locale={locale}
                        timeZone={this.props.timezone}
                        localeUtils={MomentLocaleUtils}
                        navbarElement={this.renderNavbarLeft}
                        selectedDays={[
                            fixedFrom,
                            {
                                from: fixedFrom,
                                to: fixedEnteredTo
                            }
                        ]}
                        modifiers={{
                            start: fixedFrom,
                            end: fixedEnteredTo,
                        }}
                        onDayClick={this.handleDayClick}
                    />
                </div>
                <div className="reactdaypicker-item">
                    <div className="reactdaypicker-field">
                        <input className={`form-control reactdaypicker-field__input${this.state.invalidTo ? ' is-invalid' : ''}`} type="text" name="" data-field="to"
                            value={this.state.valueTo} placeholder={placeholder}
                            title={this.state.invalidTo ? ((_parseInput(this.state.valueTo, _getTimeZone(this.props)) ? dataPicker.invalidRange : dataPicker.invalidDate) || '') : hint}
                            inputMode="numeric" autoComplete="off"
                            onChange={this.onChangeInput} onBlur={this.onCommitInput} onKeyDown={this.onKeyDownInput} />
                        <i className="fa fa-calendar glyphicon glyphicon-calendar reactdaypicker-field__icon" />
                        <div className="calendar-time" />
                    </div>
                    <DayPicker
                        ref={'rightMonth'}
                        className={'reactdaypicker__range'}
                        numberOfMonths={1}
                        navbarElement={this.renderNavbarRight}
                        initialMonth={moment.tz({},this.props.timezone).add(1, 'months').toDate()}
                        locale={locale}
                        timeZone={this.props.timezone}
                        localeUtils={MomentLocaleUtils}
                        selectedDays={[
                            fixedFrom,
                            {
                                from: fixedFrom,
                                to: fixedEnteredTo,
                            }
                        ]}
                        modifiers={{
                            start: fixedFrom,
                            end: fixedEnteredTo
                        }}
                        onDayClick={this.handleDayClick}
                    />
                </div>
            </div>
        );
    }
}

DayPickerInputRange.propTypes = {
    from: PropTypes.instanceOf(moment),
    to: PropTypes.instanceOf(moment),
    timezone: PropTypes.string,
    onAction: PropTypes.func,
    // a typed date is invalid / "from" is later than "to" (the parent disables Apply)
    onInvalid: PropTypes.func,
};

DayPickerInputRange.defaultProps = {};

export default withTranslation(DayPickerInputRange);
